import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Lock, Phone, ShieldCheck } from 'lucide-react';
import { VeoMark } from '@/components/VeoMark';
import { PasswordInput } from '@/features/auth/PasswordInput';
import { Alert, Button, Field, Input, toast } from '@/ui';
import { TechScreen } from './TechFormsPage';
import { TECH_ACCESS_TOKEN, TECH_PATHS, TECH_PHONE, TECH_TOKEN_CREATE_DATE, consumeHandoffToken, parsePhone, requestAuthCode, techStorage, verifyAuthCode } from './techSession';

/**
 * Technician sign-in for /tech, as the retired Home console had it: phone + password, then the
 * 4-digit code texted by the technician API. Technicians who arrive from the app with a token skip it.
 */
export function TechLoginPage() {
  const { t } = useTranslation('fieldops', { keyPrefix: 'techForms.login' });
  const navigate = useNavigate();
  const [phoneInput, setPhoneInput] = useState('');
  const [password, setPassword] = useState('');
  const [pendingPhone, setPendingPhone] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    consumeHandoffToken();
    if (techStorage.get(TECH_ACCESS_TOKEN)) navigate(TECH_PATHS.home, { replace: true });
  }, [navigate]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const phone = parsePhone(phoneInput);
    if (phone == null) return setError(t('phoneInvalid'));
    if (!password) return setError(t('passwordRequired'));
    setError(null);
    setBusy(true);
    try {
      await requestAuthCode({ phone, password });
      setPendingPhone(phone);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failed'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pendingPhone == null || code.length < 4) return;
    setBusy(true);
    try {
      const result = await verifyAuthCode({ phone: pendingPhone, code });
      if (!result?.token) throw new Error(t('verifyFailed'));
      techStorage.set(TECH_ACCESS_TOKEN, result.token);
      techStorage.set(TECH_TOKEN_CREATE_DATE, String(Date.now()));
      techStorage.set(TECH_PHONE, String(pendingPhone));
      if (result.requirePasswordUpdate) toast.warning(t('temporaryPassword'));
      else toast.success(t('signedIn'));
      navigate(TECH_PATHS.home, { replace: true });
    } catch (err) {
      setPendingPhone(null);
      setCode('');
      setError(err instanceof Error ? err.message : t('verifyFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TechScreen>
      <div className="rounded-xl border border-line bg-surface p-6 shadow-sm">
        <VeoMark size={36} />
        <h1 className="m-0 mt-3 font-display text-2xl font-semibold text-heading">{t('title')}</h1>
        <p className="m-0 mb-5 mt-0.5 text-base text-ink-muted">{pendingPhone == null ? t('subtitle') : t('codeSent', { phone: pendingPhone })}</p>
        {error && <Alert tone="danger" title={error} className="mb-4" />}
        {pendingPhone == null ? (
          <form onSubmit={send} noValidate className="flex flex-col gap-4">
            <Field label={t('phone')}>
              <Input prefix={<Phone />} type="tel" autoComplete="tel" inputMode="numeric" autoFocus placeholder="555-555-1234" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} />
            </Field>
            <Field label={t('password')}>
              <PasswordInput prefix={<Lock />} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-1 w-full">
              {t('sendCode')}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} noValidate className="flex flex-col gap-4">
            <Field label={t('code')}>
              <Input
                prefix={<ShieldCheck />}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                autoFocus
                className="tracking-[0.3em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </Field>
            <Button type="submit" variant="primary" size="lg" loading={busy} disabled={code.length < 4} className="mt-1 w-full">
              {t('verify')}
            </Button>
            <Button
              variant="link"
              className="self-center"
              onClick={() => {
                setPendingPhone(null);
                setCode('');
              }}
            >
              {t('back')}
            </Button>
          </form>
        )}
      </div>
      <p className="mt-4 text-center text-sm text-ink-muted">
        {t('notTechnician')}{' '}
        <a href="/login" className="font-semibold text-accent">
          {t('adminSignIn')}
        </a>
      </p>
    </TechScreen>
  );
}
